import { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface AlignmentFacade {
  alignLeft(): void;
  alignRight(): void;
  alignTop(): void;
  alignBottom(): void;
  centerOnCanvasX(target?: fabric.Object): void;
  centerOnCanvasY(target?: fabric.Object): void;
  centerFirstToOthersX(): void;
  centerFirstToOthersY(): void;
  distributeHorizontally(): void;
  distributeVertically(): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    alignment?: AlignmentFacade;
  }
}

interface Bounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

const getBounds = (obj: fabric.Object): Bounds => obj.getBoundingRect(true) as Bounds;

export function alignmentPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.ALIGNMENT,
    install(editor: FabricEditor) {
      if (!editor.selection) {
        throw new Error('alignmentPlugin requires selectionPlugin to be installed first.');
      }

      const canvas = editor.canvas;
      const selection = editor.selection;

      const finish = (modified: fabric.Object[]) => {
        for (const o of modified) o.setCoords();
        canvas.requestRenderAll();
        for (const o of modified) editor.emit('object:modified', { target: o });
      };

      const requireMulti = (): fabric.Object[] | null => {
        const active = selection.getActive();
        return active.length >= 2 ? active : null;
      };

      const facade: AlignmentFacade = {
        alignLeft() {
          const objs = requireMulti();
          if (!objs) return;
          const minLeft = objs.reduce((m, o) => Math.min(m, getBounds(o).left), Infinity);
          for (const o of objs) {
            const b = getBounds(o);
            o.setPositionByOrigin(new fabric.Point(minLeft + b.width / 2, b.top + b.height / 2), 'center', 'center');
          }
          finish(objs);
        },

        alignRight() {
          const objs = requireMulti();
          if (!objs) return;
          const maxRight = objs.reduce((m, o) => {
            const b = getBounds(o);
            return Math.max(m, b.left + b.width);
          }, -Infinity);
          for (const o of objs) {
            const b = getBounds(o);
            o.setPositionByOrigin(new fabric.Point(maxRight - b.width / 2, b.top + b.height / 2), 'center', 'center');
          }
          finish(objs);
        },

        alignTop() {
          const objs = requireMulti();
          if (!objs) return;
          const minTop = objs.reduce((m, o) => Math.min(m, getBounds(o).top), Infinity);
          for (const o of objs) {
            const b = getBounds(o);
            o.setPositionByOrigin(new fabric.Point(b.left + b.width / 2, minTop + b.height / 2), 'center', 'center');
          }
          finish(objs);
        },

        alignBottom() {
          const objs = requireMulti();
          if (!objs) return;
          const maxBottom = objs.reduce((m, o) => {
            const b = getBounds(o);
            return Math.max(m, b.top + b.height);
          }, -Infinity);
          for (const o of objs) {
            const b = getBounds(o);
            o.setPositionByOrigin(new fabric.Point(b.left + b.width / 2, maxBottom - b.height / 2), 'center', 'center');
          }
          finish(objs);
        },

        centerOnCanvasX(target) {
          const obj = target ?? canvas.getActiveObject() ?? undefined;
          if (!obj) return;
          const cw = editor.resolution.width;
          if (!cw) return;
          const b = getBounds(obj);
          obj.setPositionByOrigin(new fabric.Point(cw / 2, b.top + b.height / 2), 'center', 'center');
          finish([obj]);
        },

        centerOnCanvasY(target) {
          const obj = target ?? canvas.getActiveObject() ?? undefined;
          if (!obj) return;
          const ch = editor.resolution.height;
          if (!ch) return;
          const b = getBounds(obj);
          obj.setPositionByOrigin(new fabric.Point(b.left + b.width / 2, ch / 2), 'center', 'center');
          finish([obj]);
        },

        centerFirstToOthersX() {
          const active = selection.getActive();
          if (active.length < 2) return;
          const first = selection.getFirst() ?? active[0];
          const others = active.filter((o) => o !== first);
          if (others.length === 0) return;
          let total = 0;
          for (const o of others) {
            const b = getBounds(o);
            total += b.left + b.width / 2;
          }
          const targetCenterX = total / others.length;
          const fb = getBounds(first);
          first.setPositionByOrigin(new fabric.Point(targetCenterX, fb.top + fb.height / 2), 'center', 'center');
          finish([first]);
        },

        centerFirstToOthersY() {
          const active = selection.getActive();
          if (active.length < 2) return;
          const first = selection.getFirst() ?? active[0];
          const others = active.filter((o) => o !== first);
          if (others.length === 0) return;
          let total = 0;
          for (const o of others) {
            const b = getBounds(o);
            total += b.top + b.height / 2;
          }
          const targetCenterY = total / others.length;
          const fb = getBounds(first);
          first.setPositionByOrigin(new fabric.Point(fb.left + fb.width / 2, targetCenterY), 'center', 'center');
          finish([first]);
        },

        distributeHorizontally() {
          const active = selection.getActive();
          if (active.length < 3) return;
          const sorted = [...active].sort((a, b) => {
            const ba = getBounds(a);
            const bb = getBounds(b);
            return ba.left + ba.width / 2 - (bb.left + bb.width / 2);
          });
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const fb = getBounds(first);
          const lb = getBounds(last);
          const startC = fb.left + fb.width / 2;
          const endC = lb.left + lb.width / 2;
          const step = (endC - startC) / (sorted.length - 1);
          const moved: fabric.Object[] = [];
          for (let i = 1; i < sorted.length - 1; i++) {
            const o = sorted[i];
            const ob = getBounds(o);
            o.setPositionByOrigin(new fabric.Point(startC + step * i, ob.top + ob.height / 2), 'center', 'center');
            moved.push(o);
          }
          if (moved.length > 0) finish(moved);
        },

        distributeVertically() {
          const active = selection.getActive();
          if (active.length < 3) return;
          const sorted = [...active].sort((a, b) => {
            const ba = getBounds(a);
            const bb = getBounds(b);
            return ba.top + ba.height / 2 - (bb.top + bb.height / 2);
          });
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const fb = getBounds(first);
          const lb = getBounds(last);
          const startC = fb.top + fb.height / 2;
          const endC = lb.top + lb.height / 2;
          const step = (endC - startC) / (sorted.length - 1);
          const moved: fabric.Object[] = [];
          for (let i = 1; i < sorted.length - 1; i++) {
            const o = sorted[i];
            const ob = getBounds(o);
            o.setPositionByOrigin(new fabric.Point(ob.left + ob.width / 2, startC + step * i), 'center', 'center');
            moved.push(o);
          }
          if (moved.length > 0) finish(moved);
        },
      };

      editor.alignment = facade;
    },
    dispose(editor: FabricEditor) {
      editor.alignment = undefined;
    },
  };
}
